/* eslint-disable max-lines */
import { api } from "jiffy-api";
import { redirectTo } from "common/utils/browser";
import { isFileTypeSupported } from "common/file-type-helper";
import { writeCookie } from "common/utils/cookie";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { submitDtfEvent, submitCompletedEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageUploadStarted } from "common/utils/rudderstack/tracker";
import { generateUUID } from "common/utils/uuid";
import "common/jiffy/cable";

const DTF_CATALOG_NUMBER = "DTF001";
const UPLOADED_ARTWORK_COOKIE = "uploaded_artwork_ids";
const UPLOAD_LOCATION = "added_to_cart_modal";

export default class ModalFileUpload {
  constructor($modal) {
    this.$modal = $modal;
    this.uploadedFile = null;
    this.dtfChannelUuid = null;
    this.channelSubscription = null;
    this.channelPromise = null;
    this.uploadStartTime = null;
    this.uploadUuid = null;
  }

  get $labels() {
    return this.$modal.querySelectorAll(".js-modal-upload-label");
  }

  get $progressContainers() {
    return this.$modal.querySelectorAll(".js-modal-upload-progress");
  }

  get $progressTexts() {
    return this.$modal.querySelectorAll(".js-modal-progress-text");
  }

  get $errorContainers() {
    return this.$modal.querySelectorAll(".js-modal-upload-error");
  }

  get $errorTexts() {
    return this.$modal.querySelectorAll(".js-modal-error-text");
  }

  async initDtfChannel() {
    try {
      const response = await api.dtf.getInitConfig({ catalog_number: DTF_CATALOG_NUMBER });
      const { dtfChannelUuid } = objNamesToCamelCase(response);
      this.dtfChannelUuid = dtfChannelUuid;
      this.subscribeToChannel();
    } catch (error) {
      console.warn("Failed to initialize DTF channel", error);
    }
  }

  subscribeToChannel() {
    if (!this.dtfChannelUuid || !window.App?.cable) return;

    this.channelSubscription = window.App.cable.subscriptions.create(
      { uuid: this.dtfChannelUuid, channel: "DtfChannel" },
      {}
    );
  }

  async ensureChannelReady() {
    if (!this.dtfChannelUuid && !this.channelPromise) {
      this.channelPromise = this.initDtfChannel();
    }

    if (!this.dtfChannelUuid) {
      await this.channelPromise;
    }

    if (!this.channelSubscription) {
      this.subscribeToChannel();
    }
  }

  triggerProcessArtwork(payload) {
    if (!payload || !this.channelSubscription) return;

    try {
      this.channelSubscription.perform("process_dtf_artwork", payload);
    } catch (error) {
      console.warn("Failed to trigger DTF processing", error);
    }
  }

  handleFileSelection(file) {
    const { valid, message } = isFileTypeSupported(file, false);

    if (!valid) {
      this.showError(message);
      return false;
    }

    this.uploadedFile = file;
    return true;
  }

  createFormData() {
    const formData = new FormData();
    formData.append("generate_link", true);
    formData.append("file", this.uploadedFile);
    formData.append("catalog_number", DTF_CATALOG_NUMBER);
    formData.append("is_made", false);
    formData.append("is_made_flash", false);
    if (this.dtfChannelUuid) {
      formData.append("dtf_channel_uuid", this.dtfChannelUuid);
    }
    return formData;
  }

  showProgress() {
    this.$labels.forEach($label => $label.classList.add("hidden"));
    this.$progressContainers.forEach($container => {
      $container.classList.remove("hidden");
    });
    this.hideError();
  }

  updateProgress(text) {
    if (text) {
      this.$progressTexts.forEach($progressText => {
        const progressText = $progressText;
        progressText.textContent = text;
      });
    }
  }

  hideProgress() {
    this.$progressContainers.forEach($container => {
      $container.classList.add("hidden");
    });
    this.$labels.forEach($label => $label.classList.remove("hidden"));
  }

  showError(message) {
    this.hideProgress();
    this.$errorContainers.forEach($container => {
      $container.classList.remove("hidden");
    });
    this.$errorTexts.forEach($errorText => {
      const errorText = $errorText;
      errorText.textContent = message;
    });
  }

  hideError() {
    this.$errorContainers.forEach($container => {
      $container.classList.add("hidden");
    });
    this.$errorTexts.forEach($errorText => {
      const errorText = $errorText;
      errorText.textContent = "";
    });
  }

  processError(e) {
    const errorResponse = e.response?.data;
    const errorMessage = errorResponse?.error || e.message;
    const uploadError = errorMessage === "Network Error" ? "Upload error" : errorMessage;

    this.showError(uploadError);
  }

  buildRedirectUrl(urlString, artworkId) {
    if (!urlString) return urlString;

    try {
      const url = new URL(urlString, window.location.origin);
      if (artworkId) {
        url.searchParams.set(UPLOADED_ARTWORK_COOKIE, JSON.stringify([artworkId]));
      }
      if (this.dtfChannelUuid) {
        url.searchParams.set("dtf_channel_uuid", this.dtfChannelUuid);
      }
      return url.toString();
    } catch (error) {
      console.warn("Failed to build redirect URL", error);
      return urlString;
    }
  }

  trackUploadStarted() {
    this.uploadUuid = generateUUID();
    this.uploadStartTime = performance.now();

    submitDtfEvent("image_upload_started", {
      page: UPLOAD_LOCATION,
      catalog_number: DTF_CATALOG_NUMBER,
      type: "browse",
      file_name: this.uploadedFile.name,
      file_type: this.uploadedFile.type,
      file_size: this.uploadedFile.size,
      dpi: 300,
      upload_uuid: this.uploadUuid
    });

    trackRudderstackImageUploadStarted({
      catalog_number: DTF_CATALOG_NUMBER,
      dpi: 300,
      file_name: this.uploadedFile.name,
      file_type: this.uploadedFile.type,
      file_size: this.uploadedFile.size,
      location: UPLOAD_LOCATION,
      business_line: "Transfers",
      artworks_count: 1,
      upload_uuid: this.uploadUuid
    });
  }

  trackUploadCompleted(response) {
    submitCompletedEvent({
      file: this.uploadedFile,
      catalogNumber: DTF_CATALOG_NUMBER,
      uploadedOn: UPLOAD_LOCATION,
      multiArtwork: false,
      businessLine: "Transfers",
      dpi: 300,
      artworksCount: 1,
      uploadStartTime: this.uploadStartTime,
      artwork: response,
      uploadUuid: this.uploadUuid
    });
  }

  async uploadArtwork() {
    if (!this.uploadedFile) return;

    await this.ensureChannelReady();

    this.showProgress();
    this.updateProgress("Uploading...");
    this.trackUploadStarted();

    try {
      const response = await api.dtf.uploadArtwork(this.createFormData(), {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true
      });

      this.trackUploadCompleted(response);

      const redirectUrl = this.buildRedirectUrl(response.redirect_url, response?.id);
      if (response?.id) {
        writeCookie(UPLOADED_ARTWORK_COOKIE, JSON.stringify([response.id]), 300);
      }
      this.triggerProcessArtwork(response?.data);

      this.updateProgress("Redirecting...");
      setTimeout(() => redirectTo(redirectUrl || response.redirect_url), 500);
    } catch (e) {
      this.processError(e);
    }
  }

  resetUI() {
    this.hideProgress();
    this.hideError();
    this.uploadedFile = null;
  }
}
